import { type CSSProperties } from "react";
import { Check } from "lucide-react";
import { cn } from "./_utils";

/**
 * LifecycleStepper — the shared six-step work loop, rendered as a horizontal
 * track of dots + mono labels:
 *
 *   Proposed → Plan → Approve → Execute → Review → Done
 *
 * It is the same loop that lives in `work/workLifecycle.ts` (the Work surface's
 * `STEPS`) and the WID detail. T3 extracts it here so T7's Work-item Reader
 * (Mode C) can reuse the exact same rendering instead of re-implementing the
 * stepper inline.
 *
 * State is derived purely from the `current` step index:
 *   - steps before `current` render `done` (filled accent, check glyph),
 *   - the `current` step renders `current` (ringed, the gate marker when amber),
 *   - steps after render `upcoming` (hollow).
 *
 * The Approve step is the human gate — when it is the current step the whole
 * stepper tints amber via `gateActive`. No content is hardcoded; the labels are
 * the fixed loop vocabulary, not surface data.
 *
 * @example
 * // The current step by label — Approve is the amber human gate.
 * <LifecycleStepper current="Approve" testId="wid-lifecycle" />
 *
 * @example
 * // …or by the zero-based phase index (clamps out-of-range values).
 * <LifecycleStepper current={STEP_INDEX[wi.phase]} />
 */

/** The canonical six steps of the work loop, in order. */
export const LIFECYCLE_STEPS = [
  "Proposed",
  "Plan",
  "Approve",
  "Execute",
  "Review",
  "Done",
] as const;

export type LifecycleStep = (typeof LIFECYCLE_STEPS)[number];

/** The index Approve occupies — the human-gate step. */
const APPROVE_INDEX = LIFECYCLE_STEPS.indexOf("Approve");

export interface LifecycleStepperProps {
  /**
   * The current step. Accepts the step label or its zero-based index. Steps
   * before it read as done; steps after read as upcoming. Out-of-range / unknown
   * values clamp so the stepper never crashes on unexpected phase data.
   */
  current: LifecycleStep | number;
  /** Forwarded to the root so detail testids survive. */
  testId?: string;
  className?: string;
}

function resolveCurrentIndex(current: LifecycleStep | number): number {
  if (typeof current === "number") {
    if (!Number.isFinite(current)) return 0;
    return Math.max(0, Math.min(LIFECYCLE_STEPS.length - 1, Math.round(current)));
  }
  const idx = LIFECYCLE_STEPS.indexOf(current);
  return idx === -1 ? 0 : idx;
}

export function LifecycleStepper({ current, testId, className }: LifecycleStepperProps) {
  const currentIndex = resolveCurrentIndex(current);
  const gateActive = currentIndex === APPROVE_INDEX;

  return (
    <div
      className={cn("lc-stepper", gateActive && "lc-gate", className)}
      data-testid={testId}
      role="list"
      aria-label="Lifecycle"
    >
      {LIFECYCLE_STEPS.map((step, i) => {
        const state = i < currentIndex ? "done" : i === currentIndex ? "current" : "upcoming";
        const isGateStep = i === APPROVE_INDEX;
        return (
          <div className="lc-step" key={step} role="listitem" aria-current={state === "current" ? "step" : undefined}>
            <span
              className={cn(
                "lc-dot",
                state === "done" && "lc-dot-done",
                state === "current" && "lc-dot-current",
                isGateStep && state === "current" && "lc-dot-gate",
              )}
              aria-hidden
            >
              {state === "done" ? <Check width={8} height={8} strokeWidth={3.5} /> : null}
            </span>
            <span className="lc-label">{step}</span>
            {i < LIFECYCLE_STEPS.length - 1 && (
              <span
                className={cn("lc-rail", i < currentIndex && "lc-rail-done")}
                style={i < currentIndex ? ({ "--lc-rail": "var(--dc, var(--accent))" } as CSSProperties) : undefined}
                aria-hidden
              />
            )}
          </div>
        );
      })}
    </div>
  );
}
